import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { format, parseISO } from 'date-fns';

import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { FormField, DatePicker } from '@/shared/ui';
import { SimpleSelect, type SelectOption } from '@/shared/ui/filters';
import type { ReceiveConfirmContextDto } from '@/entities/receive';

import { CONFIRM_ARRIVAL_TEXT_MAX_LENGTH } from '../model/constants';
import { ConfirmArrivalLossExplanation } from './confirm-arrival-loss-explanation';
import type { ConfirmArrivalFormValues } from '../model/types';

interface ConfirmArrivalHeaderFieldsProps {
  form: UseFormReturn<ConfirmArrivalFormValues>;
  context: ReceiveConfirmContextDto;
  isRefreshingContext: boolean;
  onLocationChange: (locationId: string) => void;
  /** A waybill receipt: names the waybill, the truck and every consignment it covers. */
  isWaybill?: boolean;
}

/** `yyyy-MM-dd` ↔ `Date`, parsed as local time so the day never shifts. */
const parseDate = (value: string): Date | null => {
  if (!value) return null;
  const parsed = parseISO(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export function ConfirmArrivalHeaderFields({
  form,
  context,
  isRefreshingContext,
  onLocationChange,
  isWaybill = false
}: ConfirmArrivalHeaderFieldsProps) {
  const { t } = useTranslation();
  const {
    control,
    register,
    formState: { errors }
  } = form;

  const locationOptions: SelectOption[] = useMemo(
    () =>
      context.receiveLocations.map((location) => ({
        value: location.locationId,
        label: location.name
      })),
    [context.receiveLocations]
  );

  const storageAreaOptions: SelectOption[] = useMemo(
    () =>
      context.storageAreas.map((area) => ({
        value: String(area.seqno),
        label: area.name
      })),
    [context.storageAreas]
  );

  const minDate = parseDate(context.minReceiveDate) ?? undefined;
  const maxDate = parseDate(context.maxReceiveDate) ?? undefined;

  return (
    <div className="space-y-6">
      {/* `grid-cols-1` is not redundant with a bare `grid`. Without it the single
          column below `sm` is an *implicit* track sized `auto`, which stretches to
          its items' max-content — and a label carries `truncate`, hence
          `white-space: nowrap`, so its max-content is the entire string and it
          pushes the modal wide instead of ellipsing. Naming the column gives it
          the same bounded `minmax(0,1fr)` track that already makes
          `sm:grid-cols-2` behave. */}
      <div className="grid grid-cols-1 gap-x-6 gap-y-7 sm:grid-cols-2">
        {isWaybill && (
          <>
            <FormField label={t('waybill.title.label', 'Waybill')} name="waybill">
              <Input id="waybill" value={context.waybill || '—'} disabled readOnly />
            </FormField>

            <FormField label={t('journey.vehicleIdnf.label', 'Vehicle Id')} name="vehicleIdentifier">
              <Input id="vehicleIdentifier" value={context.vehicleIdentifier || '—'} disabled readOnly />
            </FormField>

            <FormField
              label={t('journey.participatingRequestsStr.label', 'Consignments')}
              name="consignments"
              className="sm:col-span-2">
              <Input
                id="consignments"
                value={(context.shipments ?? []).map((shipment) => shipment.consignmentNumber).join(', ') || '—'}
                disabled
                readOnly
              />
            </FormField>
          </>
        )}

        <FormField label={t('journey.origin.label', 'Origin Location')} name="originLocation">
          <Input id="originLocation" value={context.originLocation || '—'} disabled readOnly />
        </FormField>

        <FormField
          label={t('receive.receivelocation.label', 'Receive Location')}
          name="targetLocationId"
          error={errors.targetLocationId?.message}
          required>
          <Controller
            name="targetLocationId"
            control={control}
            render={({ field }) => (
              <SimpleSelect
                options={locationOptions}
                value={field.value || undefined}
                onChange={(value) => onLocationChange(value ?? '')}
                isLoading={isRefreshingContext}
                placeholder={t('receive.receivelocation.label', 'Receive Location')}
                className="w-full"
              />
            )}
          />
        </FormField>

        <FormField
          label={t('receive.receivedate.label', 'Actual Date of Receipt')}
          name="actualDateOfReceipt"
          error={errors.actualDateOfReceipt?.message}
          required>
          <Controller
            name="actualDateOfReceipt"
            control={control}
            render={({ field }) => (
              <DatePicker
                id="actualDateOfReceipt"
                value={parseDate(field.value)}
                onChange={(date) => field.onChange(date ? format(date, 'yyyy-MM-dd') : '')}
                dateFormat="dd/MMM/yyyy"
                minDate={minDate}
                maxDate={maxDate}
              />
            )}
          />
        </FormField>

        {/* Storage areas only exist on some locations; when the backend reports
            `storageAreaRequired` the choice is mandatory. */}
        {storageAreaOptions.length > 0 && (
          <FormField
            label={t('receive.receivelocation.space.label', 'Storage Area')}
            name="targetLocationSpaceSeq"
            error={errors.targetLocationSpaceSeq?.message}
            required={context.storageAreaRequired}>
            <Controller
              name="targetLocationSpaceSeq"
              control={control}
              render={({ field }) => (
                <SimpleSelect
                  options={storageAreaOptions}
                  value={field.value != null ? String(field.value) : undefined}
                  onChange={(value) => field.onChange(value ? Number(value) : null)}
                  placeholder={t('receive.receivelocation.space.label', 'Storage Area')}
                  className="w-full"
                />
              )}
            />
          </FormField>
        )}

        <FormField
          label={t('receive.document.label', 'Transport Documentation Number')}
          name="transportDocNumber"
          error={errors.transportDocNumber?.message}>
          <Input
            {...register('transportDocNumber')}
            id="transportDocNumber"
            maxLength={CONFIRM_ARRIVAL_TEXT_MAX_LENGTH}
            placeholder={t('receive.document.desc', 'I.e. Way Bill Number, Goods receive Note Number, etc...')}
          />
        </FormField>

        <FormField
          label={t('receive.comments.label', 'Comments')}
          name="comments"
          error={errors.comments?.message}
          className="sm:col-span-2">
          <Textarea
            {...register('comments')}
            id="comments"
            rows={2}
            maxLength={CONFIRM_ARRIVAL_TEXT_MAX_LENGTH}
            placeholder={t('receive.comments.label', 'Comments')}
          />
        </FormField>
      </div>

      <ConfirmArrivalLossExplanation form={form} />
    </div>
  );
}
